import React, { useState, useMemo } from "react";
import {
  CalendarDays, RefreshCw, Filter, LineChart, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, ListovaniMesice, Dlazdice, ErrorNote, Hlaska, SkeletonCards } from "../components/ui.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, RadkyPrehledu, RadekPrehledu, SkupinaPrehledu,
} from "../components/PrehledDlazdice.jsx";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { useApiData } from "../lib/api.js";
import { fmt } from "../lib/format.js";

// 13.9.2026 (fáze 4f, rozhodnutí Č1): stránka je poskládaná ze vzorníku.
// Nahoře hlavička s názvem, stavem týdne v rohu ikony a Obnovit, pod ní
// listování týdny jako listování měsíci ve Výplatách, pak čtyři čísla jako
// dlaždice a dvě karty s tmavou hlavičkou. Obsah se nezměnil, jen skladba.

function getMonday(date) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function formatIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatCzDate(d) {
  return `${d.getDate()}.${d.getMonth() + 1}.`;
}

// Datum pondělí ze ISO stringu: "21.4." Na dlaždici se celý rozsah nevejde,
// a začátek týdne stačí, číslo týdne je hned vedle.
function formatWeekStart(isoStart) {
  if (!isoStart) return "";
  const a = new Date(isoStart + "T00:00:00");
  if (isNaN(a.getTime())) return "";
  return `${a.getDate()}.${a.getMonth() + 1}.`;
}

function getWeekNumber(d) {
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  return 1 + Math.ceil((target - firstThursday) / (7 * 24 * 60 * 60 * 1000));
}

// Data jsou sledovaná od března 2026 (první pondělí v březnu = 2.3.2026)
const MIN_WEEK_START = "2026-03-02"; // pondělí W10 2026 (začátek března)

// Stav týdne. Barva tečky v rohu ikony stránky plus text, který se ukáže
// po klepnutí na ni.
const STAVY = {
  current: {
    tecka: "ok",
    napoveda: "Tenhle týden právě probíhá, čísla se ještě mění. Ustálí se v pondělí, až se týden uzavře.",
  },
  past: {
    tecka: "neutralni",
    napoveda: "Týden je uzavřený, čísla se už nemění.",
  },
  future: {
    tecka: "prazdna",
    napoveda: "Budoucí týden. Data se počítají až od jeho prvního dne.",
  },
};

// Jedna dlaždice ve srovnání CPL. Barva říká, jak je týden na tom: nad 900 Kč
// draho, pod 700 Kč levně, vybraný týden značkově.
function DlazdiceCpl({ w, predchozi, vybrany, maxCpl }) {
  const delta = predchozi && predchozi.cpl > 0
    ? Math.round(((w.cpl - predchozi.cpl) / predchozi.cpl) * 100)
    : null;
  const hure = delta !== null && delta > 0;
  const lepe = delta !== null && delta < 0;
  const IkonaTrendu = hure ? TrendingUp : lepe ? TrendingDown : Minus;
  const barvaTrendu = hure ? "text-rose-600" : lepe ? "text-emerald-700" : "text-slate-400";
  const sirkaPruhu = maxCpl > 0 ? Math.round((w.cpl / maxCpl) * 100) : 0;
  const uroven = w.cpl > 900 ? "draho" : w.cpl > 0 && w.cpl < 700 ? "levne" : "stred";
  const ramecek = vybrany
    ? "border-2 border-brand bg-brand-light"
    : uroven === "draho"
      ? "border border-rose-200 bg-rose-50"
      : uroven === "levne"
        ? "border border-emerald-200 bg-emerald-50"
        : "border border-slate-200 bg-white";
  const barvaPruhu = vybrany
    ? "bg-brand"
    : uroven === "draho" ? "bg-rose-400" : uroven === "levne" ? "bg-emerald-400" : "bg-slate-400";

  return (
    <div className={`rounded-xl p-3 ${ramecek}`}>
      <div className="flex items-baseline justify-between gap-1">
        <span className="min-w-0 truncate text-sm font-bold text-slate-700">
          {w.week}
          {w.weekStart && !vybrany && (
            <span className="ml-1 text-xs font-normal text-slate-400">od {formatWeekStart(w.weekStart)}</span>
          )}
        </span>
        {vybrany && (
          <span className="shrink-0 rounded-full bg-brand px-1.5 py-0.5 text-[11px] font-bold text-white">tento</span>
        )}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
        {fmt(w.cpl)} <span className="text-sm font-normal text-slate-500">Kč</span>
      </div>
      {delta !== null ? (
        <div className={`mt-0.5 flex items-center gap-1 text-xs font-semibold demo-sensitive ${barvaTrendu}`}>
          <IkonaTrendu className="h-3 w-3" />
          {delta > 0 ? "+" : ""}{delta} %
        </div>
      ) : (
        <div className="mt-0.5 text-xs text-slate-400">první v řadě</div>
      )}
      <div className="mt-1 text-[11px] tabular-nums text-slate-500">
        {w.leads} leadů, {fmt(w.spend)} Kč
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/70">
        <div className={`h-full rounded-full ${barvaPruhu}`} style={{ width: `${sirkaPruhu}%` }} />
      </div>
    </div>
  );
}

export default function Weekly() {
  const todayMonday = useMemo(() => getMonday(new Date()), []);
  const [weekOffset, setWeekOffset] = useState(0);
  const { blurred } = useDemoMode();

  const selectedMonday = useMemo(() => {
    const d = new Date(todayMonday);
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [todayMonday, weekOffset]);

  const selectedSunday = useMemo(() => {
    const d = new Date(selectedMonday);
    d.setDate(d.getDate() + 6);
    return d;
  }, [selectedMonday]);

  const isFuture = weekOffset > 0;
  const weekStartIso = formatIsoDate(selectedMonday);
  const isBeforeMin = weekStartIso < MIN_WEEK_START;

  const { data, loading, error, refreshing, refetch } = useApiData(
    (isFuture || isBeforeMin) ? null : `/api/data-weekly?weekStart=${weekStartIso}`,
  );

  const weekLabel = `W${getWeekNumber(selectedMonday)}, ${formatCzDate(selectedMonday)} až ${formatCzDate(selectedSunday)}`;
  const status = isFuture ? "future" : (weekOffset === 0 ? "current" : "past");
  const stav = STAVY[status];

  const predchoziMozny = weekStartIso > MIN_WEEK_START;
  const oTydenZpet = () => {
    const prev = new Date(selectedMonday);
    prev.setDate(prev.getDate() - 7);
    if (formatIsoDate(prev) >= MIN_WEEK_START) setWeekOffset(weekOffset - 1);
  };

  // Funnel. Trial jsou realizované trialy v týdnu, Deal / Prohra / Čekají jsou
  // aktuální stage leadů vzniklých v tomto týdnu (lead-atribuce).
  const leads = data?.leads || 0;
  const nqLeads = data?.nqLeads || 0;
  const realized = data?.trialLeadsThisWeek ?? 0;
  const deal = data?.dealThisWeek ?? 0;
  const loss = data?.lossThisWeek ?? 0;
  const undecided = Math.max(0, realized - deal - loss);
  const leadToTrialPct = leads > 0 ? Math.round((realized / leads) * 100) : 0;
  const leadToNqPct = leads > 0 ? Math.round((nqLeads / leads) * 100) : 0;
  const trialToDealPct = realized > 0 ? Math.round((deal / realized) * 100) : 0;
  const successPct = (deal + loss) > 0 ? Math.round((deal / (deal + loss)) * 100) : 0;

  const trend = data?.cplTrend || [];
  const maxCpl = Math.max(...trend.map((w) => w.cpl), 1);

  return (
    <Stranka className={blurred ? "demo-blur" : ""}>
      <GradientHeader
        title="Týdenní přehled"
        icon={CalendarDays}
        loading={loading || refreshing}
        napoveda={stav.napoveda}
        tecka={stav.tecka}
        akce={[
          {
            klic: "obnovit",
            ikona: RefreshCw,
            popis: "Obnovit",
            onClick: refetch,
            disabled: loading || refreshing || isFuture || isBeforeMin,
          },
        ]}
      />

      <ListovaniMesice
        popis={weekLabel}
        onZpet={oTydenZpet}
        onVpred={() => setWeekOffset(weekOffset + 1)}
        zpetVypnuto={!predchoziMozny}
        vpredVypnuto={weekOffset >= 0}
        naDnesek={weekOffset !== 0 ? () => setWeekOffset(0) : undefined}
        popisDneska="Na tento týden"
      />

      {isFuture && (
        <Hlaska ton="pozor" title="Budoucí týden">
          Data se počítají až od prvního dne daného týdne.
        </Hlaska>
      )}
      {isBeforeMin && (
        <Hlaska ton="pozor" title="Starší týden">
          Data jsou sledovaná od března 2026, starší týdny nejsou k dispozici.
        </Hlaska>
      )}

      {!isFuture && !isBeforeMin && loading && <SkeletonCards count={4} />}

      {!isFuture && !isBeforeMin && error && !loading && (
        <ErrorNote onRetry={refetch}>Nepodařilo se načíst data za týden: {error}</ErrorNote>
      )}

      {!isFuture && !isBeforeMin && data && !loading && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Dlazdice
              popisek="Leady"
              hodnota={fmt(leads)}
              ton="znacka"
              doplnky={nqLeads > 0 ? <span className="text-xs text-slate-500">z toho {nqLeads} nekvalifikovaných</span> : null}
            />
            <Dlazdice
              popisek="Realizované trialy"
              hodnota={fmt(realized)}
              ton="info"
              doplnky={<span className="text-xs text-slate-500">{leadToTrialPct} % z leadů</span>}
            />
            <Dlazdice
              popisek="MKT spend"
              hodnota={`${fmt(data.mktSpend)} Kč`}
              ton="pozor"
              doplnky={<span className="text-xs text-slate-500">{formatCzDate(selectedMonday)} až {formatCzDate(selectedSunday)}</span>}
            />
            <Dlazdice
              popisek="Ø CPL"
              hodnota={`${fmt(data.cpl)} Kč`}
              ton="ok"
              doplnky={<span className="text-xs text-slate-500">cena za jeden lead</span>}
            />
          </div>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Filter}
              nazev="Funnel týdne"
              podtitulek="od leadu k podpisu"
              odznak={`${fmt(leads)} leadů`}
              napoveda="Trial jsou realizované trialy v tomto týdnu. Deal, Prohra a Čekají jsou aktuální stage leadů, které v tomto týdnu vznikly, takže jejich součet sedí na počet trialů."
            />
            <RadkyPrehledu>
              <RadekPrehledu popisek="Leady" hodnota={fmt(leads)} />
              {nqLeads > 0 && (
                <RadekPrehledu
                  popisek="Nekvalifikované leady"
                  napoveda="Leady, které neprošly kvalifikací, trial se z nich nedomlouvá."
                  hodnota={fmt(nqLeads)}
                  doplnek={`${leadToNqPct} % z leadů`}
                  tonHodnoty="text-rose-700"
                />
              )}
              <RadekPrehledu
                popisek="Realizované trialy"
                hodnota={fmt(realized)}
                doplnek={`${leadToTrialPct} % z leadů`}
              />
            </RadkyPrehledu>

            <SkupinaPrehledu
              nazev="Jak trialy dopadly"
              napoveda="Lead-atribuce: počítají se leady vzniklé v tomto týdnu a jejich dnešní stage, ne dealy uzavřené tento týden ze starších leadů."
            />
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Deal"
                napoveda="Leady vytvořené tento týden, které už mají v Leady stage Deal nebo Uzavřeno."
                hodnota={fmt(deal)}
                doplnek={`${trialToDealPct} % z trialů`}
                tonHodnoty="text-emerald-700"
              />
              <RadekPrehledu
                popisek="Prohra"
                napoveda="Leady vytvořené tento týden, které už mají v Leady stage Prohra. Nepočítají se prohry ze starších leadů, jejichž trial proběhl tento týden."
                hodnota={fmt(loss)}
                tonHodnoty="text-rose-700"
              />
              <RadekPrehledu
                popisek="Čekají na rozhodnutí"
                napoveda="Leady vytvořené tento týden, které prošly trialem, ale ještě se o nich nerozhodlo. Deal plus Prohra plus Čekají se rovná počtu realizovaných trialů."
                hodnota={fmt(undecided)}
              />
              {(deal > 0 || loss > 0) && (
                <RadekPrehledu
                  popisek="Úspěšnost rozhodnutých"
                  napoveda="Deal dělený součtem Deal a Prohra. Čekající se do toho nepočítají."
                  hodnota={`${successPct} %`}
                  tonHodnoty={successPct >= 70 ? "text-emerald-700" : successPct >= 50 ? "text-amber-700" : "text-rose-700"}
                />
              )}
            </RadkyPrehledu>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={LineChart}
              nazev="Vývoj CPL"
              podtitulek="poslední 4 týdny"
              napoveda="CPL je průměrná cena za jeden lead: MKT spend za týden dělený počtem nových leadů. Rostoucí číslo znamená, že se kampaně zdražují nebo hůř konvertují. Nad 900 Kč je dlaždice červená, pod 700 Kč zelená."
            />
            <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4 sm:gap-2.5 sm:p-4">
              {trend.map((w, i) => (
                <DlazdiceCpl
                  key={w.week + i}
                  w={w}
                  predchozi={i > 0 ? trend[i - 1] : null}
                  vybrany={w.weekStart === weekStartIso}
                  maxCpl={maxCpl}
                />
              ))}
            </div>
          </KartaPrehledu>
        </>
      )}
    </Stranka>
  );
}
